import React, { useState, useRef } from 'react';
import { Heart, Clock, MapPin, Users, ArrowRight, MessageCircle, Play, Pause, Sparkles, Gift, Star } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalendar from './BookingCalendar';
import { Seo } from './Seo';

const VIDEO_URL = '/lunchtime-massage-v5.mp4';

const HERO_IMG = 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxvaWwlMjBtYXNzYWdlJTIwYmFjayUyMGJlYXV0aWZ1bCUyMHdvbWFuJTIwc3BhJTIwY2FuZGxlJTIwd2FybSUyMGxpZ2h0fGVufDB8fHx8MTc3ODY5ODAzNHww&ixlib=rb-4.1.0&q=85';

const GALLERY = [
  { src: 'https://images.unsplash.com/photo-1702312721918-62235a0b77d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwc3BhJTIwZGF5JTIwd29tZW4lMjByZWxheGluZyUyMG1hc3NhZ2UlMjB0b2dldGhlciUyMGx1eHVyeXxlbnwwfHx8fDE3Nzg2OTc5ODh8MA&ixlib=rb-4.1.0&q=85', alt: 'Friends relaxing at spa together' },
  { src: 'https://images.unsplash.com/photo-1607713128667-f7608c154725?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxvaWwlMjBtYXNzYWdlJTIwYmFjayUyMGJlYXV0aWZ1bCUyMHdvbWFuJTIwc3BhJTIwY2FuZGxlJTIwd2FybSUyMGxpZ2h0fGVufDB8fHx8MTc3ODY5ODAzNHww&ixlib=rb-4.1.0&q=85', alt: 'Candlelit massage treatment' },
  { src: 'https://images.unsplash.com/photo-1749132403012-24034a90a7ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHx0aGFpJTIwbWFzc2FnZSUyMHRoZXJhcGlzdCUyMG9pbCUyMHRyZWF0bWVudCUyMGVuY2xvc2VkJTIwcm9vbSUyMHdhcm0lMjBkaW18ZW58MHx8fHwxNzc2MTIwODQwfDA&ixlib=rb-4.1.0&q=85', alt: 'Thai massage in warm room' },
  { src: 'https://images.unsplash.com/photo-1706795033917-dee116e7cba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxjb3VwbGVzJTIwbWFzc2FnZSUyMHNwYSUyMHNpZGUlMjBieSUyMHNpZGUlMjByb21hbnRpYyUyMHJlbGF4aW5nJTIwY2FuZGxlc3xlbnwwfHx8fDE3Nzg2OTc5ODd8MA&ixlib=rb-4.1.0&q=85', alt: 'Spa candles and towels' },
  { src: 'https://images.unsplash.com/photo-1639758597299-81abeebc5f9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjB3b21hbiUyMHNwYSUyMG1hc3NhZ2UlMjByZWxheGluZyUyMGZhY2UlMjBzZXJlbmV8ZW58MHx8fHwxNzc2MTIxNDc4fDA&ixlib=rb-4.1.0&q=85', alt: 'Serene woman spa relaxation' },
  { src: 'https://images.unsplash.com/photo-1559185590-d545a0c5a1dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxmcmllbmRzJTIwc3BhJTIwZGF5JTIwd29tZW4lMjByZWxheGluZyUyMG1hc3NhZ2UlMjB0b2dldGhlciUyMGx1eHVyeXxlbnwwfHx8fDE3Nzg2OTc5ODh8MA&ixlib=rb-4.1.0&q=85', alt: 'Friends enjoying spa treatment' },
];

const SHARE_MSG = `Treat someone special to a Couples Massage at Dublin Holistic Massage!\n\n60 Minutes — €120 per person\n90 Minutes — €150 per person\n\nPerfect for couples, friends, sisters, mother & daughter — or anyone you love spending time with.\n\nBook together. Relax together.\n\nSave 15% when you pay in full online — or 5% with a €20 deposit!\n\nTwo locations: City Centre & Clarehall\n\nBook: www.dublinholisticmassages.com/couples\nWhatsApp: +353 85 774 7474`;

const AUDIENCES = [
  { label: 'Couples', icon: Heart },
  { label: 'Friends', icon: Users },
  { label: 'Sisters', icon: Sparkles },
  { label: 'Mother & Daughter', icon: Heart },
  { label: 'Male Friends', icon: Users },
];

const CouplesPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f0d]" data-testid="couples-page">
      <Seo
        title="Couples Massage Dublin | Side-by-Side Massage for Two | Dublin Holistic Massage"
        description="Couples massage in Dublin — relax side by side with two therapists at our Clarehall studio (free parking) or city centre. 60 & 90 minute sessions. Perfect gift. Book online!"
      />

      {/* ─── HERO ─── */}
      <section className="relative h-[85vh] min-h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Couples massage" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0a0f0d]" />
        </div>

        <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
          <div className="inline-flex items-center gap-2 bg-rose-500/20 text-rose-300 px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-widest mb-6 border border-rose-500/30 backdrop-blur-sm">
            <Heart size={14} className="fill-rose-400" />
            Dublin Holistic Massage
          </div>

          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-4">
            Relax Together
          </h1>
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-rose-300/90 italic mb-6">
            Couples Massage Experience
          </p>
          <p className="text-white/70 text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-8">
            Share the gift of relaxation with someone special. Side by side, tension melts away.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => setBookingOpen(true)}
              className="bg-rose-600 hover:bg-rose-500 text-white rounded-full py-6 px-10 text-sm uppercase tracking-wider font-bold group"
              data-testid="couples-hero-book"
            >
              Book Your Couples Massage
              <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => window.open(`https://wa.me/353857747474?text=${encodeURIComponent('Hi! I\'d like to book a couples massage.')}`, '_blank')}
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white rounded-full py-6 px-10 text-sm font-medium border border-white/20"
              data-testid="couples-hero-whatsapp"
            >
              <MessageCircle size={18} className="mr-2" />
              WhatsApp Us
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* ─── WHO IS IT FOR ─── */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-3">Perfect For Everyone You Love</h2>
          <p className="text-white/50 text-sm mb-10">Book together. Relax together. Reconnect.</p>

          <div className="flex flex-wrap justify-center gap-4">
            {AUDIENCES.map((a) => (
              <div key={a.label} className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 flex items-center gap-3 hover:border-rose-500/40 hover:bg-rose-500/5 transition-all cursor-default">
                <a.icon size={20} className="text-rose-400" />
                <span className="text-white text-sm font-medium">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── VIDEO SECTION ─── */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-2">Experience The Difference</h2>
            <p className="text-white/50 text-sm">Step into a world of calm and connection</p>
          </div>

          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 cursor-pointer group" onClick={toggleVideo}>
            <video
              ref={videoRef}
              poster={HERO_IMG}
              loop muted playsInline preload="metadata"
              className="w-full h-[350px] sm:h-[450px] lg:h-[500px] object-cover"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              data-testid="couples-video"
            >
              <source src={VIDEO_URL} type="video/mp4" />
            </video>
            <div className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-300 ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
              <div className="w-20 h-20 bg-white/15 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 hover:scale-110 transition-transform">
                {isPlaying ? <Pause size={32} className="text-white" /> : <Play size={32} className="text-white ml-1" />}
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
              <p className="text-white text-lg font-display">A moment of peace, shared with someone special</p>
            </div>
          </div>

          {/* Share video via WhatsApp */}
          <div className="flex justify-center mt-6 gap-4">
            <Button
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_MSG)}`, '_blank')}
              className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full py-4 px-8 text-sm font-medium"
              data-testid="couples-share-whatsapp"
            >
              <MessageCircle size={18} className="mr-2" />
              Share This Page
            </Button>
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className="py-16 px-4 sm:px-6 lg:px-12" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.08) 50%, transparent 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-2">Choose Your Experience</h2>
            <p className="text-white/50 text-sm">All prices are per person</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* 60 min */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 text-center hover:border-rose-500/40 transition-colors group">
              <Clock size={28} className="text-rose-400 mx-auto mb-4" />
              <h3 className="font-display text-xl text-white mb-1">60 Minute Session</h3>
              <p className="text-white/50 text-sm mb-6">Perfect for a relaxing escape</p>
              <div className="font-display text-5xl text-rose-400 mb-1">€120</div>
              <p className="text-white/40 text-xs mb-2">per person</p>
              <p className="text-emerald-400 text-xs font-medium mb-6">Save 15% when you pay in full online!</p>
              <Button
                onClick={() => setBookingOpen(true)}
                className="w-full bg-rose-600 hover:bg-rose-500 text-white rounded-full py-5 text-sm font-semibold group-hover:shadow-lg group-hover:shadow-rose-500/20 transition-all"
                data-testid="couples-book-60"
              >
                Book 60 Minutes
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>

            {/* 90 min */}
            <div className="relative bg-gradient-to-br from-rose-900/20 to-rose-800/5 backdrop-blur-sm border-2 border-rose-500/40 rounded-3xl p-8 text-center group">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-rose-600 text-white text-[10px] font-bold uppercase px-4 py-1 rounded-full tracking-wider">
                Most Popular
              </div>
              <Star size={28} className="text-rose-400 mx-auto mb-4" />
              <h3 className="font-display text-xl text-white mb-1">90 Minute Session</h3>
              <p className="text-white/50 text-sm mb-6">The ultimate relaxation experience</p>
              <div className="font-display text-5xl text-rose-400 mb-1">€150</div>
              <p className="text-white/40 text-xs mb-2">per person</p>
              <p className="text-emerald-400 text-xs font-medium mb-6">Save 15% when you pay in full online!</p>
              <Button
                onClick={() => setBookingOpen(true)}
                className="w-full bg-rose-600 hover:bg-rose-500 text-white rounded-full py-5 text-sm font-semibold group-hover:shadow-lg group-hover:shadow-rose-500/20 transition-all"
                data-testid="couples-book-90"
              >
                Book 90 Minutes
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── GALLERY ─── */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-2">Your Relaxation Awaits</h2>
            <p className="text-white/50 text-sm">A glimpse into your couples experience</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {GALLERY.map((img) => (
              <div key={img.src} className={`rounded-2xl overflow-hidden border border-white/10 hover:border-rose-500/30 transition-all ${GALLERY.indexOf(img) === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}>
                <img src={img.src} alt={img.alt} className={`w-full object-cover ${GALLERY.indexOf(img) === 0 ? 'h-48 md:h-full' : 'h-36 sm:h-48'}`} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LOCATIONS ─── */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-2">Two Locations Across Dublin</h2>
            <p className="text-white/50 text-sm">Choose the one closest to you</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center hover:border-rose-500/30 transition-colors">
              <MapPin size={24} className="text-rose-400 mx-auto mb-3" />
              <h3 className="font-display text-lg text-white mb-1">City Centre</h3>
              <p className="text-white/60 text-sm mb-1">19 Talbot Street, Dublin 1</p>
              <p className="text-white/40 text-xs">Heart of Dublin — easy access</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center hover:border-rose-500/30 transition-colors">
              <MapPin size={24} className="text-rose-400 mx-auto mb-3" />
              <h3 className="font-display text-lg text-white mb-1">Clarehall</h3>
              <p className="text-white/60 text-sm mb-1">Clare Village Apartments, Donaghmede</p>
              <p className="text-white/40 text-xs">Free parking & shower facilities</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHAT'S INCLUDED ─── */}
      <section className="py-16 px-4 sm:px-6 lg:px-12" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.06) 50%, transparent 100%)' }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-2">What's Included</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Side-by-side massage experience',
              'Choice of massage style',
              'Experienced professional therapists',
              'Warm, intimate private setting',
              'Aromatic essential oils',
              'Complete relaxation & stress relief',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-4">
                <Heart size={16} className="text-rose-400 flex-shrink-0" />
                <span className="text-white/80 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GIFT CTA ─── */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-rose-900/30 to-rose-800/10 border border-rose-500/30 rounded-3xl p-8 sm:p-12 text-center">
            <Gift size={36} className="text-rose-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-3">Give The Gift Of Togetherness</h2>
            <p className="text-white/60 text-sm max-w-lg mx-auto mb-8">
              Surprise someone with a couples massage gift voucher. The perfect present for birthdays, anniversaries, or just because.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setBookingOpen(true)}
                className="bg-rose-600 hover:bg-rose-500 text-white rounded-full py-6 px-10 text-sm uppercase tracking-wider font-bold group"
                data-testid="couples-cta-book"
              >
                Book Now
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => window.open(`https://wa.me/353857747474?text=${encodeURIComponent('Hi! I\'d like to book a couples massage as a gift.')}`, '_blank')}
                className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full py-6 px-10 text-sm font-medium"
                data-testid="couples-cta-whatsapp"
              >
                <MessageCircle size={18} className="mr-2" />
                WhatsApp +353 85 774 7474
              </Button>
            </div>

            <p className="text-rose-300/60 text-xs mt-6">www.dublinholisticmassages.com</p>
          </div>
        </div>
      </section>

      {/* ─── BOTTOM SHARE BAR ─── */}
      <section className="py-10 px-4 border-t border-white/10">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4">
          <p className="text-white/50 text-sm">Know someone who'd love this?</p>
          <Button
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_MSG)}`, '_blank')}
            className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full py-4 px-8 text-sm font-medium"
            data-testid="couples-bottom-share"
          >
            <MessageCircle size={18} className="mr-2" />
            Share via WhatsApp
          </Button>
        </div>
      </section>

      <BookingCalendar isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
};

export default CouplesPage;
